import { useIsMobile } from "@universe/ui/hooks/use-mobile";
import { cn } from "@universe/ui/lib/utils";
import { useTranslations } from "next-intl";

import {
  BankTransactionState,
  FilteredResolvedBlockchainWithdrawal,
  PaymentWithdrawalState,
  PricingQuoteState,
} from "@generated/types";

export type TransactionStatusBadgeType =
  | BankTransactionState
  | FilteredResolvedBlockchainWithdrawal["withdrawalState"]
  | PricingQuoteState
  | PaymentWithdrawalState
  | "COMMITTED";

interface TransactionStatusBadgeProps {
  status: TransactionStatusBadgeType;
  className?: string;
  isReturned?: boolean;
  size?: "xs" | "sm";
  mobileSize?: "xs" | "sm";
  showReconciled?: boolean;
}

export default function TransactionStatusBadge({
  status,
  className,
  isReturned,
  size = "sm",
  mobileSize = "xs",
  showReconciled = false,
}: TransactionStatusBadgeProps) {
  const t = useTranslations();
  const isMobile = useIsMobile();

  if (status === "RECONCILED" && !showReconciled) return null;

  if (isReturned) {
    return (
      <p
        className={cn(
          "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200",
          size === "xs" && "text-xs",
          size === "sm" && "text-sm",
          isMobile && mobileSize === "xs" && "text-xs",
          isMobile && mobileSize === "sm" && "text-sm",
          className
        )}
      >
        {isMobile
          ? t("ledger.status.returnedMobile")
          : t("ledger.status.returned")}
      </p>
    );
  }

  const processingStates = [
    "ACCEPTED",
    "WAITING",
    "READY",
    "PROCESSING",
    "PENDING",
    "UNRECONCILED",
    "SCHEDULED",
    "FUNDS_CONFIRMED",
  ];

  const isProcessing = processingStates.includes(status as string);

  const getStatusStyles = () => {
    switch (status as TransactionStatusBadgeType) {
      case processingStates[0]:
      case processingStates[1]:
      case processingStates[2]:
      case processingStates[3]:
      case processingStates[4]:
      case processingStates[5]:
      case processingStates[6]:
      case processingStates[7]:
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "REJECTED":
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-200";
      case "RECONCILED":
      case "COMMITTED":
      case "RETURNED":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-brand-primary/10 text-gray-800 border-line";
    }
  };

  const getStatusText = () => {
    switch (status as TransactionStatusBadgeType) {
      case processingStates[0]:
      case processingStates[1]:
      case processingStates[2]:
      case processingStates[3]:
      case processingStates[4]:
      case processingStates[5]:
      case processingStates[6]:
      case processingStates[7]:
        return t("ledger.status.processing");
      case "REJECTED":
      case "CANCELLED":
        return isMobile
          ? t("ledger.status.cancelledMobile")
          : t("ledger.status.cancelled");
      case "RECONCILED":
      case "COMMITTED":
        return t("ledger.status.completed");
      case "RETURNED":
        return isMobile
          ? t("ledger.status.returnedMobile")
          : t("ledger.status.returned");
      default:
        return status;
    }
  };

  const statusText = getStatusText();

  return (
    statusText && (
      <div>
        <p
          className={cn(
            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
            size === "xs" && "text-xs",
            size === "sm" && "text-sm",
            isMobile && mobileSize === "xs" && "text-xs",
            isMobile && mobileSize === "sm" && "text-sm",
            getStatusStyles(),
            isProcessing && "animate-pulse",
            className
          )}
        >
          {statusText}
        </p>
      </div>
    )
  );
}
